import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { error } from '../../../../ErrorHandling';
import { errorAlert, FlashAlert } from '../../../../Notifications/Types';
import { TPlainTextKey } from '../../../../Translations';
import {
  LearningDesignsResource,
  ProductLicenseResource,
} from '../../../LearningCollections/Types';
import { LearningDesignsState, loading, ready, waiting } from './Types';

class LearningDesignsStore {
  @observable state: LearningDesignsState = waiting();

  constructor() {
    makeObservable(this);
  }

  @action
  loading = (selectedProductLicenseResource: Maybe<ProductLicenseResource>) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'loading':
        this.state = loading(selectedProductLicenseResource);
        break;
      case 'error':
        break;
    }
  };

  @action
  ready = (learningDesignsResource: LearningDesignsResource) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'error':
        break;
      case 'loading':
        this.state = ready(learningDesignsResource, this.state.selectedProductLicenseResource);
        break;
    }
  };

  @computed
  get learningDesignsResource(): Maybe<LearningDesignsResource> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return nothing();
      case 'ready':
        return just(this.state.learningDesignsResource);
    }
  }

  @computed
  get productLicenseResource(): Maybe<ProductLicenseResource> {
    switch (this.state.kind) {
      case 'waiting':
      case 'error':
        return nothing();
      case 'loading':
      case 'ready':
        return this.state.selectedProductLicenseResource;
    }
  }

  @computed
  get backButtonDisabled(): boolean {
    switch (this.state.kind) {
      case 'waiting':
      case 'error':
      case 'loading':
        return true;
      case 'ready':
        return false;
    }
  }

  @action
  error = (msg: TPlainTextKey) => {
    this.state = error(msg);
  };

  @computed
  get notification(): Maybe<FlashAlert> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
        return nothing();
      case 'error':
        return just(this.state).map(errorAlert);
    }
  }

  @computed
  get currentPage(): number {
    switch (this.state.kind) {
      case 'loading':
        return this.state.page;
      case 'ready':
        return this.state.learningDesignsResource.metadata.currentPage;
      case 'waiting':
      case 'error':
        return 1;
    }
  }

  @action
  pageTo = (page: number) => {
    switch (this.state.kind) {
      case 'waiting':
        this.state = loading(nothing(), page);
        break;
      case 'ready':
        this.state = loading(this.state.selectedProductLicenseResource, page);
        break;
      case 'loading':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };
}

export default LearningDesignsStore;
